import React, { useEffect, useState, useRef, lazy, memo, createElement } from 'react';
import { CheckIcon, ChevronDownIcon, XIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { HeroSection } from '../components/HeroSection';
import { NotACashGrabSection } from '../components/NotACashGrabSection';
import { ChillsSection } from '../components/ChillsSection';
import { WhatsInsideSection } from '../components/WhatsInsideSection';
import { SocialProofSection } from '../components/SocialProofSection';
import { AverageRatingSection } from '../components/AverageRatingSection';
import { TestimonialsGridSection } from '../components/TestimonialsGridSection';
import { UrgencySection } from '../components/UrgencySection';
import { FAQSection } from '../components/FAQSection';
import { Footer } from '../components/Footer';
import { SocialProofNotification } from '../components/SocialProofNotification';
// ─── Checkout URL builder ─────────────────────────────────────────────────────
const buildUrl = (template: string) => {
  const fbclid = new URLSearchParams(window.location.search).get('fbclid');
  return fbclid ? template.replace('{{fbclid}}', fbclid) : template;
};
// ─── Tracking ─────────────────────────────────────────────────────────────────
let icFiredV3 = false;
const trackV3 = (name: string, price: number) => {
  if (typeof window.fbq !== 'function') return;
  window.fbq('track', 'AddToCart', {
    content_name: name,
    value: price,
    currency: 'USD'
  });
  if (!icFiredV3) {
    icFiredV3 = true;
    window.fbq('track', 'InitiateCheckout', {
      content_name: name,
      value: price,
      currency: 'USD'
    });
  }
};
let pricingViewFiredV3 = false;
const trackPricingViewV3 = () => {
  if (pricingViewFiredV3) return;
  if (sessionStorage.getItem('pricing_section_viewed')) return;
  pricingViewFiredV3 = true;
  sessionStorage.setItem('pricing_section_viewed', 'true');
  if (typeof window.fbq === 'function') {
    window.fbq('track', 'ViewContent', {
      content_name: 'Pricing Section — Complete Digital Collection',
      value: 59.97,
      currency: 'USD'
    });
  }
};
// ─── Design tokens ────────────────────────────────────────────────────────────
const GOLD = '#D4AF37';
const NAVY = '#0B1B3D';
const NAVY_ALT = '#0F2347';
// V3 theme — matches V1 (bttf) palette
const V3_ACCENT = '#FF6C00'; // bttf-primary orange
const V3_ACCENT_GLOW = 'rgba(255,108,0,0.3)';
const V3_BG = '#0B0B0B'; // bttf-bg
const V3_CARD_BG = '#141414'; // slightly lighter dark for bump cards
const V3_TEXT_DARK = '#0B0B0B'; // for text on light backgrounds
// ─── Checkout URLs ────────────────────────────────────────────────────────────
const CHECKOUT_DIGITAL = 'https://legendaryreads.com/cart/42231842701394:1?checkout&fbclid={{fbclid}}';
const CHECKOUT_PREMIUM = 'https://legendaryreads.com/cart/42285346947154:1?checkout&fbclid={{fbclid}}';
const CHECKOUT_BUNDLE = 'https://legendaryreads.com/cart/42285352288338:1?checkout&fbclid={{fbclid}}';
const CHECKOUT_BASIC = 'https://legendaryreads.com/cart/42185022439506:1?checkout&fbclid={{fbclid}}';
const BASE_PRICE = 59.97;
const BUMP1_PRICE = 35;
const BUMP2_PRICE_STANDALONE = 220;
const BUMP2_PRICE_WITH_BUMP1 = 185;
const BUMP1_ITEMS = ['Everything in the Complete Digital Collection', 'Back In Time — Premium Hardcover Edition', 'Delivery in 12 days'];
const BUMP2_ITEMS = ['Everything in the Complete Digital Collection', 'All 4 volumes in Paperback Edition', 'Back In Time Audiobook Experience', 'Hill Valley 1985 Limited Vintage Trucker Hat', "Flux Energy Core Collector's Framed Print — 10×10 Limited Edition", 'Time Machine Heritage Tote', '40 Years Travelling Through Time Commemorative Mug', 'Fast Delivery'];
const BUNDLE_IMAGES = ['https://cdn.magicpatterns.com/uploads/2mCBcB1vUDxS6qefycewPD/TIME_TRAVELLERS_COLLECTOR_BUNDLED.png', 'https://cdn.magicpatterns.com/uploads/7BQJLvNZKyzy2Jn4rjWLvk/bit_audiobook.png', 'https://cdn.magicpatterns.com/uploads/8av42GqLUN9WRwXAhxHRBi/Generated_Image_November_18,_2025_-_3_14PM.png', 'https://uploadthingy.s3.us-west-1.amazonaws.com/i3DKchuX8RMmzYrqAhpK13/foam-trucker-hat-black-one-size-right-front-6914c3a4781f3.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/rbwPKtwrCHTaFNqFeaH2fA/enhanced-matte-paper-framed-poster-%28in%29-white-10x10-front-6914bc8d8b378.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/2eQBo88LwYLe7TLxEHMDQe/white-glossy-mug-white-11-oz-handle-on-left-6914d9d1888e4.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/dsPxuWW7a4Xja3nWKMPU8M/all-over-print-tote-bag-yellow-15x15-mockup-69123afa061d2.jpg'];
// ─── Image lightbox ───────────────────────────────────────────────────────────
const ImageLightbox = ({
  images,
  startIndex,
  onClose
}: {
  images: string[];
  startIndex: number;
  onClose: () => void;
}) => {
  const [idx, setIdx] = useState(startIndex);
  const prev = () => setIdx(i => i === 0 ? images.length - 1 : i - 1);
  const next = () => setIdx(i => i === images.length - 1 ? 0 : i + 1);
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4" onClick={onClose} data-id="element-869">
      <div className="relative w-full max-w-lg" onClick={e => e.stopPropagation()} data-id="element-870">
        <button onClick={onClose} className="absolute -top-10 right-0 text-white/70 hover:text-white transition-colors" aria-label="Close" data-id="element-871">
          <XIcon className="h-7 w-7" data-id="element-872" />
        </button>
        <div className="rounded-xl overflow-hidden shadow-2xl bg-black" style={{
        aspectRatio: '1/1'
      }} data-id="element-873">
          <img src={images[idx]} alt={`Item ${idx + 1}`} className="w-full h-full object-contain" decoding="async" data-id="element-874" />
        </div>
        <button onClick={prev} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full transition-all" aria-label="Previous" data-id="element-875">
          <ChevronLeftIcon className="h-5 w-5" data-id="element-876" />
        </button>
        <button onClick={next} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full transition-all" aria-label="Next" data-id="element-877">
          <ChevronRightIcon className="h-5 w-5" data-id="element-878" />
        </button>
        <div className="flex justify-center gap-1.5 mt-3" data-id="element-879">
          {images.map((_, i) => <button key={i} onClick={() => setIdx(i)} className="rounded-full transition-all duration-200" style={{
          width: i === idx ? '20px' : '8px',
          height: '8px',
          backgroundColor: i === idx ? GOLD : 'rgba(255,255,255,0.3)'
        }} data-id="element-880" />)}
        </div>
      </div>
    </div>;
};
// ─── Shared UI primitives ─────────────────────────────────────────────────────
const GoldButton = ({
  href,
  onClick,
  children,
  small = false
}: {
  href: string;
  onClick?: () => void;
  children: React.ReactNode;
  small?: boolean;
}) => <a href={href} onClick={onClick} className="cta block w-full text-center font-bold tracking-wide rounded-lg transition-all duration-200 active:scale-[0.98] no-underline hover:brightness-110 shadow-lg font-body" style={{
  minHeight: small ? '44px' : '52px',
  padding: small ? '10px 16px' : '14px 24px',
  fontSize: small ? '14px' : '16px',
  background: 'linear-gradient(90deg, #FFD300 0%, #FF6C00 100%)',
  color: '#0B0B0B',
  fontFamily: 'Inter, sans-serif',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}} data-id="element-881">
    {children}
  </a>;
const BumpItem = ({
  text
}: {
  text: string;
}) => <li className="flex items-start gap-2" data-id="element-882">
    <span style={{
    color: V3_ACCENT,
    flexShrink: 0,
    fontSize: '9px',
    marginTop: '5px'
  }} data-id="element-883">
      ✦
    </span>
    <span className="text-xs leading-snug" style={{
    color: 'rgba(245,245,245,0.78)',
    fontFamily: 'Inter, sans-serif'
  }} data-id="element-884">
      {text}
    </span>
  </li>;
// ─── V2 Pricing block (used in V3) ───────────────────────────────────────────
const PurchaseV3 = () => {
  const [bump1, setBump1] = useState(false);
  const [bump2, setBump2] = useState(false);
  const [bump2Expanded, setBump2Expanded] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxStart, setLightboxStart] = useState(0);
  const [bump1LightboxOpen, setBump1LightboxOpen] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const hasTrackedRef = useRef(false);
  const bump2Price = bump1 ? BUMP2_PRICE_WITH_BUMP1 : BUMP2_PRICE_STANDALONE;
  const total = BASE_PRICE + (bump1 ? BUMP1_PRICE : 0) + (bump2 ? bump2Price : 0);
  const shopPayInstallment = (Math.ceil(total / 4 * 100) / 100).toFixed(2);
  const checkoutUrl = buildUrl(bump2 ? CHECKOUT_BUNDLE : bump1 ? CHECKOUT_PREMIUM : CHECKOUT_DIGITAL);
  const handleCta = () => {
    const name = bump2 ? "Time Traveler's Collector Bundle" : bump1 ? 'Premium Hardcover Bundle' : 'Complete Digital Collection';
    trackV3(name, total);
  };
  const openLightbox = (e: React.MouseEvent, idx = 0) => {
    e.stopPropagation();
    setLightboxStart(idx);
    setLightboxOpen(true);
  };
  // Scroll to CTA on mobile when a bump is added
  const scrollToCta = () => {
    if (window.innerWidth < 768 && ctaRef.current) {
      setTimeout(() => {
        ctaRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 50);
    }
  };
  useEffect(() => {
    if (!sectionRef.current || hasTrackedRef.current) return;
    const isMobile = window.innerWidth < 768;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !hasTrackedRef.current) {
          hasTrackedRef.current = true;
          trackPricingViewV3();
          const delay = isMobile ? 1000 : 500;
          setTimeout(() => {
            const ctaTrigger = document.createElement('div');
            ctaTrigger.className = 'cta';
            ctaTrigger.style.cssText = 'position:absolute;opacity:0;pointer-events:none;width:1px;height:1px;top:0;left:0;';
            ctaTrigger.setAttribute('data-pricing-trigger', 'true');
            document.body.appendChild(ctaTrigger);
            const clickEvent = new MouseEvent('click', {
              view: window,
              bubbles: true,
              cancelable: true
            });
            ctaTrigger.dispatchEvent(clickEvent);
            if (isMobile) {
              const touchStartEvent = new TouchEvent('touchstart', {
                bubbles: true,
                cancelable: true,
                view: window
              });
              const touchEndEvent = new TouchEvent('touchend', {
                bubbles: true,
                cancelable: true,
                view: window
              });
              ctaTrigger.dispatchEvent(touchStartEvent);
              ctaTrigger.dispatchEvent(touchEndEvent);
            }
            setTimeout(() => {
              if (ctaTrigger.parentNode) ctaTrigger.parentNode.removeChild(ctaTrigger);
            }, 2000);
          }, delay);
        }
      });
    }, {
      threshold: isMobile ? 0.1 : 0.3,
      rootMargin: '0px'
    });
    observer.observe(sectionRef.current);
    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
    };
  }, []);
  return <section ref={sectionRef} style={{
    backgroundColor: V3_BG,
    paddingTop: '32px',
    paddingBottom: '48px',
    scrollMarginTop: '64px'
  }} className="md:py-20" id="pricing-top" data-id="element-885">
      <div className="container mx-auto px-4 md:px-10 max-w-2xl" data-id="element-886">
        <h2 className="text-[22px] md:text-4xl font-bold text-center mb-4 md:mb-10 font-future tracking-wide bg-gradient-main text-transparent bg-clip-text" data-id="element-887">
          Special 40th Anniversary Collector's Offer
        </h2>

        {/* ── MAIN PRODUCT CARD ── */}
        <div className="rounded-xl mb-3 shadow-[0_8px_40px_rgba(0,0,0,0.7)]" style={{
        backgroundColor: '#1a1a1a',
        border: '1px solid rgba(255,108,0,0.2)'
      }} data-id="element-888">
          {/* MOBILE */}
          <div className="md:hidden px-4 py-4" data-id="element-889">
            <p className="text-[11px] font-bold text-center uppercase tracking-widest mb-3 font-future" style={{
            color: V3_ACCENT,
            letterSpacing: '0.06em'
          }} data-id="element-890">
              The Complete Digital Collection
            </p>
            <div className="flex gap-3 items-center mb-3" data-id="element-891">
              <img src="https://cdn.magicpatterns.com/uploads/drsCGCcawJQmxdkJUsQyKs/Holiday_Edition_Digital_Set.png" alt="Complete Digital Collection" className="rounded-lg shadow-md flex-shrink-0" style={{
              width: '100px',
              height: '100px',
              objectFit: 'cover'
            }} loading="lazy" decoding="async" data-id="element-892" />
              <div className="flex-1 grid grid-cols-1 gap-y-1" data-id="element-893">
                {['Back In Time (190 pg)', 'Behind The Clock Tower (87 pg)', 'Great Scott! (87 pg)', 'Time. Imagination. Destiny. (71 pg)'].map(item => <div key={item} className="flex items-start gap-1.5" data-id="element-894">
                    <span style={{
                  color: V3_ACCENT,
                  fontSize: '8px',
                  flexShrink: 0,
                  marginTop: '4px'
                }} data-id="element-895">
                      ✦
                    </span>
                    <span className="text-[11px] leading-tight" style={{
                  color: '#F5F5F5',
                  fontFamily: 'Inter, sans-serif'
                }} data-id="element-896">
                      {item}
                    </span>
                  </div>)}
              </div>
            </div>
            <div className="text-center mb-2" data-id="element-897">
              <span className="line-through text-sm opacity-40 mr-2" style={{
              color: '#F5F5F5',
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-898">
                $114.97
              </span>
              <span className="font-bold text-[26px] leading-none transition-all duration-300 bg-gradient-main text-transparent bg-clip-text" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-899">
                ${total.toFixed(2)}
              </span>
              {(bump1 || bump2) && <span className="block text-[10px] text-bttf-subtext mt-0.5" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-900">
                  w/ selected add-ons
                </span>}
            </div>
            <div className="text-center mb-2" data-id="element-901">
              <span className="inline-flex items-center gap-1 bg-green-500/15 border border-green-500/30 text-green-400 text-xs font-bold px-2.5 py-1 rounded-full" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-902">
                You Save ${(114.97 - total).toFixed(2).replace('-', '')}
              </span>
            </div>
            {/* Shop Pay installments */}
            <div className="mb-3 px-3 py-2 bg-black/30 rounded-lg border border-white/10 text-center" data-id="element-903">
              <div className="flex items-center justify-center gap-2 mb-0.5" data-id="element-904">
                <span className="text-gray-300 text-xs" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-905">
                  or 4 payments of
                </span>
                <span className="text-bttf-accent font-bold text-sm" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-906">
                  ${shopPayInstallment}
                </span>
              </div>
              <div className="flex items-center justify-center gap-1.5" data-id="element-907">
                <span className="text-gray-400 text-xs" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-908">
                  with
                </span>
                <img src="https://cdn.magicpatterns.com/uploads/sSzxmJmvJ5ddbjuz1Lh65R/Shop_Pay_logo_purple.png" alt="Shop Pay" className="h-5 inline-block" width="60" height="20" loading="lazy" decoding="async" data-id="element-909" />
              </div>
              <p className="text-gray-400 text-[10px] mt-0.5" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-910">
                Biweekly interest-free payments
              </p>
            </div>
            {/* CTA ref anchor for mobile scroll */}
            <div ref={ctaRef} data-id="element-911">
              <GoldButton href={checkoutUrl} onClick={handleCta} data-id="element-912">
                GET MY COLLECTION →
              </GoldButton>
            </div>
            <p className="text-center text-bttf-subtext text-[10px] mt-1.5" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-913">
              Instant delivery. Readable on any device.
            </p>
          </div>

          {/* DESKTOP */}
          <div className="hidden md:block p-10" data-id="element-914">
            <h3 className="text-2xl font-bold text-center mb-6 font-future tracking-wide" style={{
            color: '#F5F5F5',
            letterSpacing: '0.04em'
          }} data-id="element-915">
              THE COMPLETE DIGITAL COLLECTION
            </h3>
            <div className="flex justify-center mb-6" data-id="element-916">
              <img src="https://cdn.magicpatterns.com/uploads/drsCGCcawJQmxdkJUsQyKs/Holiday_Edition_Digital_Set.png" alt="Complete Digital Collection" className="rounded-xl shadow-md" style={{
              maxWidth: '260px',
              width: '100%'
            }} loading="lazy" decoding="async" data-id="element-917" />
            </div>
            <ul className="space-y-3 mb-6" data-id="element-918">
              {['Back In Time (190 pages) — The Cultural Analysis', 'Behind the Clock Tower (87 pages) — The Production Story', 'Great Scott! (87 pages) — 40 Reflections for 40 Years', 'Time. Imagination. Destiny. (71 pages) — The Philosophical Legacy'].map(item => <li key={item} className="flex items-start gap-3" data-id="element-919">
                  <span className="font-bold mt-0.5" style={{
                color: V3_ACCENT,
                flexShrink: 0
              }} data-id="element-920">
                    ✦
                  </span>
                  <span className="text-base leading-relaxed" style={{
                color: '#F5F5F5',
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-921">
                    {item}
                  </span>
                </li>)}
            </ul>
            <div className="text-center mb-2" data-id="element-922">
              <span className="line-through opacity-40 mr-2 text-lg" style={{
              color: '#F5F5F5',
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-923">
                $114.97
              </span>
              <span className="font-bold text-3xl transition-all duration-300 bg-gradient-main text-transparent bg-clip-text" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-924">
                ${total.toFixed(2)}
              </span>
            </div>
            {(bump1 || bump2) && <p className="text-center text-xs mb-2 text-bttf-subtext" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-925">
                Includes selected add-ons
              </p>}
            <div className="text-center mb-3" data-id="element-926">
              <span className="inline-flex items-center gap-1 bg-green-500/15 border border-green-500/30 text-green-400 text-sm font-bold px-3 py-1.5 rounded-full" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-927">
                You Save ${(114.97 - total).toFixed(2).replace('-', '')}
              </span>
            </div>
            {/* Shop Pay installments */}
            <div className="mb-4 px-4 py-2.5 bg-black/30 rounded-lg border border-white/10 text-center max-w-xs mx-auto" data-id="element-928">
              <div className="flex items-center justify-center gap-2 mb-0.5" data-id="element-929">
                <span className="text-gray-300 text-xs" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-930">
                  or 4 payments of
                </span>
                <span className="text-bttf-accent font-bold text-sm" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-931">
                  ${shopPayInstallment}
                </span>
              </div>
              <div className="flex items-center justify-center gap-1.5" data-id="element-932">
                <span className="text-gray-400 text-xs" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-933">
                  with
                </span>
                <img src="https://cdn.magicpatterns.com/uploads/sSzxmJmvJ5ddbjuz1Lh65R/Shop_Pay_logo_purple.png" alt="Shop Pay" className="h-5 inline-block" width="60" height="20" loading="lazy" decoding="async" data-id="element-934" />
              </div>
              <p className="text-gray-400 text-[10px] mt-0.5" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-935">
                Biweekly interest-free payments
              </p>
            </div>
            <GoldButton href={checkoutUrl} onClick={handleCta} data-id="element-936">
              GET MY COLLECTION →
            </GoldButton>
            <p className="text-center text-bttf-subtext text-xs mt-3" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-937">
              Instant delivery. Readable on any device.
            </p>
          </div>
        </div>

        {/* ── ORDER BUMP 1 ── */}
        <div className="rounded-xl mb-3 overflow-hidden transition-all duration-300" style={{
        border: bump1 ? `3px solid ${V3_ACCENT}` : '3px solid rgba(255,108,0,0.4)',
        backgroundColor: bump1 ? 'rgba(255,108,0,0.07)' : '#111111',
        boxShadow: bump1 ? `0 0 28px ${V3_ACCENT_GLOW}` : '0 2px 12px rgba(0,0,0,0.5)'
      }} data-id="element-938">
          <div className="px-4 py-2" style={{
          backgroundColor: bump1 ? V3_ACCENT : 'rgba(255,108,0,0.18)'
        }} data-id="element-939">
            <span className="text-xs font-bold uppercase tracking-widest font-body" style={{
            color: bump1 ? '#0B0B0B' : V3_ACCENT,
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-940">
              ⚡ Special One-Time Offer
            </span>
          </div>
          <div className="px-4 pt-3 pb-4" data-id="element-941">
            <div className="flex items-start gap-3 mb-3 cursor-pointer" onClick={() => {
            const next = !bump1;
            setBump1(next);
            if (next) scrollToCta();
          }} data-id="element-942">
              <div className="flex-shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center transition-all duration-200" style={{
              borderColor: V3_ACCENT,
              backgroundColor: bump1 ? V3_ACCENT : 'transparent',
              minWidth: '24px',
              marginTop: '1px'
            }} data-id="element-943">
                {bump1 && <CheckIcon className="h-4 w-4" style={{
                color: '#0B0B0B'
              }} data-id="element-944" />}
              </div>
              <div data-id="element-945">
                <p className="font-bold text-bttf-text text-sm md:text-base leading-snug" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-946">
                  Add the Premium Hardcover Edition{' '}
                  <span style={{
                  color: V3_ACCENT
                }} data-id="element-947">
                    — Only +${BUMP1_PRICE} Today
                  </span>
                </p>
                <p className="text-bttf-subtext text-xs mt-0.5 italic" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-948">
                  Own it. Display it. Keep it forever.
                </p>
              </div>
            </div>
            <div className="flex gap-3 items-start mb-3" data-id="element-949">
              {/* Image + label — image wrapper is relative so zoom hint is scoped to image */}
              <div className="flex-shrink-0 cursor-pointer group" onClick={e => {
              e.stopPropagation();
              setBump1LightboxOpen(true);
            }} data-id="element-950">
                <div className="relative w-[100px] h-[100px] md:w-[130px] md:h-[130px]" data-id="element-951">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/pSD1kcciQ8RbkUdnckm8uJ/PREMIUM_EDITION_V1.2.png" alt="Premium Hardcover Edition — tap to enlarge" className="rounded-lg shadow-lg w-full h-full object-cover transition-opacity group-hover:opacity-80" loading="lazy" decoding="async" data-id="element-952" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-lg" data-id="element-953">
                    <div className="bg-black/50 rounded-full p-1.5" data-id="element-954">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-955">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0zm-6-3v6m-3-3h6" data-id="element-956" />
                      </svg>
                    </div>
                  </div>
                </div>
                <p className="text-center text-[9px] mt-1 leading-tight w-[100px] md:w-[130px]" style={{
                color: V3_ACCENT,
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-957">
                  Tap to enlarge
                </p>
              </div>
              <ul className="space-y-1.5 flex-1" data-id="element-958">
                {BUMP1_ITEMS.map(item => <BumpItem key={item} text={item} data-id="element-959" />)}
              </ul>
            </div>
            <div className="w-full text-center font-bold py-3 rounded-lg transition-all duration-200 text-sm cursor-pointer select-none" style={{
            backgroundColor: bump1 ? V3_ACCENT : 'transparent',
            border: `2px solid ${V3_ACCENT}`,
            color: bump1 ? '#0B0B0B' : V3_ACCENT,
            fontFamily: 'Inter, sans-serif'
          }} onClick={() => {
            const next = !bump1;
            setBump1(next);
            if (next) scrollToCta();
          }} data-id="element-960">
              {bump1 ? '✓ Added to my order' : `Add Premium Hardcover — Only +$${BUMP1_PRICE}`}
            </div>
          </div>
        </div>

        {/* ── ORDER BUMP 2 ── */}
        <div className="rounded-xl mb-5 overflow-hidden transition-all duration-300" style={{
        border: bump2 ? `3px solid ${V3_ACCENT}` : '3px solid rgba(255,108,0,0.4)',
        backgroundColor: bump2 ? 'rgba(255,108,0,0.07)' : '#111111',
        boxShadow: bump2 ? `0 0 28px ${V3_ACCENT_GLOW}` : '0 2px 12px rgba(0,0,0,0.5)'
      }} data-id="element-961">
          <div className="px-4 py-2" style={{
          backgroundColor: bump2 ? V3_ACCENT : 'rgba(255,108,0,0.18)'
        }} data-id="element-962">
            <span className="text-xs font-bold uppercase tracking-widest" style={{
            color: bump2 ? '#0B0B0B' : V3_ACCENT,
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-963">
              🚀 Ultimate Upgrade
            </span>
          </div>
          <div className="px-4 pt-3 pb-4" data-id="element-964">
            <div className="flex items-start gap-3 mb-3 cursor-pointer" onClick={() => {
            const next = !bump2;
            setBump2(next);
            if (next) scrollToCta();
          }} data-id="element-965">
              <div className="flex-shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center transition-all duration-200" style={{
              borderColor: V3_ACCENT,
              backgroundColor: bump2 ? V3_ACCENT : 'transparent',
              minWidth: '24px',
              marginTop: '1px'
            }} data-id="element-966">
                {bump2 && <CheckIcon className="h-4 w-4" style={{
                color: '#0B0B0B'
              }} data-id="element-967" />}
              </div>
              <div data-id="element-968">
                <p className="font-bold text-bttf-text text-sm md:text-base leading-snug" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-969">
                  Upgrade to the Complete Collector's Bundle{' '}
                  <span style={{
                  color: V3_ACCENT
                }} data-id="element-970">
                    — Only +${bump2Price} Today
                    {bump1 && <span className="ml-2 text-xs font-normal text-bttf-subtext line-through" data-id="element-971">
                        ${BUMP2_PRICE_STANDALONE}
                      </span>}
                  </span>
                </p>
                <p className="text-bttf-subtext text-xs mt-0.5 italic" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-972">
                  Every volume. Every format. The full archive.
                </p>
              </div>
            </div>
            <div className="flex gap-3 items-start mb-2" data-id="element-973">
              {/* Image + label — image wrapper is relative so thumbnails are scoped to image */}
              <div className="flex-shrink-0 cursor-pointer group" onClick={e => openLightbox(e, 0)} data-id="element-974">
                <div className="relative w-[100px] h-[100px] md:w-[130px] md:h-[130px]" data-id="element-975">
                  <img src={BUNDLE_IMAGES[0]} alt="Time Traveler's Collector Bundle — tap to see all items" className="rounded-lg shadow-lg transition-opacity group-hover:opacity-80 w-full h-full object-cover" loading="lazy" decoding="async" data-id="element-976" />
                  {/* Thumbnail hint overlay — now correctly scoped inside image wrapper */}
                  <div className="absolute bottom-1 right-1 bg-black/70 rounded px-1 py-0.5 flex gap-0.5" data-id="element-977">
                    {[1, 2, 3].map(i => <div key={i} className="rounded-sm overflow-hidden" style={{
                    width: '12px',
                    height: '12px'
                  }} data-id="element-978">
                        <img src={BUNDLE_IMAGES[i]} alt="" className="w-full h-full object-cover" decoding="async" data-id="element-979" />
                      </div>)}
                    <span className="text-white text-[8px] ml-0.5 leading-none self-center" data-id="element-980">
                      +{BUNDLE_IMAGES.length - 4}
                    </span>
                  </div>
                </div>
                {/* Label is now outside the relative image wrapper — no overlap */}
                <p className="text-center text-[9px] mt-1 leading-tight w-[100px] md:w-[130px]" style={{
                color: V3_ACCENT,
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-981">
                  Tap to see all items
                </p>
              </div>
              <div className="flex-1" data-id="element-982">
                <ul className="space-y-1.5" data-id="element-983">
                  {BUMP2_ITEMS.slice(0, bump2Expanded ? BUMP2_ITEMS.length : 4).map(item => <BumpItem key={item} text={item} data-id="element-984" />)}
                </ul>
                {BUMP2_ITEMS.length > 4 && <button type="button" className="flex items-center gap-1 text-[11px] mt-1.5" style={{
                color: V3_ACCENT,
                fontFamily: 'Inter, sans-serif',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0
              }} onClick={e => {
                e.stopPropagation();
                setBump2Expanded(!bump2Expanded);
              }} data-id="element-985">
                    <ChevronDownIcon className={`h-3 w-3 transition-transform ${bump2Expanded ? 'rotate-180' : ''}`} data-id="element-986" />
                    {bump2Expanded ? 'Show less' : `+${BUMP2_ITEMS.length - 4} more items`}
                  </button>}
              </div>
            </div>
            <div className="w-full text-center font-bold py-3 rounded-lg transition-all duration-200 text-sm cursor-pointer select-none mt-2" style={{
            backgroundColor: bump2 ? V3_ACCENT : 'transparent',
            border: `2px solid ${V3_ACCENT}`,
            color: bump2 ? '#0B0B0B' : V3_ACCENT,
            fontFamily: 'Inter, sans-serif'
          }} onClick={() => {
            const next = !bump2;
            setBump2(next);
            if (next) scrollToCta();
          }} data-id="element-987">
              {bump2 ? '✓ Upgrade added' : `Upgrade to Collector Bundle — Only +$${bump2Price}`}
            </div>
          </div>
        </div>

        {/* ── FALLBACK ── */}
        <div className="rounded-xl py-4 px-5 text-center" style={{
        backgroundColor: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,108,0,0.15)'
      }} data-id="element-988">
          <p className="text-bttf-subtext text-xs italic mb-3" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-989">
            Just want the core retrospective?
          </p>
          <div className="flex items-center justify-center gap-3 mb-3" data-id="element-990">
            <img src="https://cdn.magicpatterns.com/uploads/ewSrEYsf3d6aAxsfRJbJ5B/BACK_IN_TIME_-_DIGITAL11v2.png" alt="Digital Edition" className="rounded-md" style={{
            width: '52px',
            height: '52px',
            objectFit: 'cover',
            opacity: 0.6
          }} loading="lazy" decoding="async" data-id="element-991" />
            <div className="text-left" data-id="element-992">
              <p className="text-bttf-subtext text-sm font-medium" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-993">
                Digital Edition
              </p>
              <p className="text-bttf-subtext text-sm" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-994">
                $39.97
              </p>
            </div>
          </div>
          <a href={buildUrl(CHECKOUT_BASIC)} onClick={() => trackV3('Digital Edition', 39.97)} className="text-bttf-subtext text-xs underline hover:text-bttf-text transition-colors" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-995">
            Get the Digital Edition
          </a>
        </div>
      </div>

      {lightboxOpen && <ImageLightbox images={BUNDLE_IMAGES} startIndex={lightboxStart} onClose={() => setLightboxOpen(false)} data-id="element-996" />}
      {bump1LightboxOpen && <ImageLightbox images={['https://uploadthingy.s3.us-west-1.amazonaws.com/pSD1kcciQ8RbkUdnckm8uJ/PREMIUM_EDITION_V1.2.png']} startIndex={0} onClose={() => setBump1LightboxOpen(false)} data-id="element-997" />}
    </section>;
};
// ─── LandingV3 export ─────────────────────────────────────────────────────────
export const LandingV3 = () => <div className="w-full min-h-screen bg-bttf-bg text-bttf-text" data-id="element-998">
    <Header data-id="element-999" />
    <main data-id="element-1000">
      <HeroSection data-id="element-1001" />
      <NotACashGrabSection data-id="element-1002" />
      <ChillsSection data-id="element-1003" />
      <WhatsInsideSection data-id="element-1004" />
      <SocialProofSection data-id="element-1005" />
      <PurchaseV3 data-id="element-1006" />
      <AverageRatingSection data-id="element-1007" />
      <TestimonialsGridSection data-id="element-1008" />
      <UrgencySection data-id="element-1009" />
      <FAQSection data-id="element-1010" />
    </main>
    <Footer data-id="element-1011" />
    <SocialProofNotification data-id="element-1012" />
  </div>;